import React, { useState, useCallback, useRef } from 'react';
import { CheckCircle2, XCircle, X } from 'lucide-react';

/**
 * Reusable toast notification component.
 *
 * Usage:
 *   import { Toast, useToast } from '../shared/Toast';
 *
 *   function MyPage() {
 *     const { message, showSuccess, showError, dismiss } = useToast();
 *     // ...
 *     showSuccess('Item added successfully!');
 *     showError('Something went wrong.');
 *     // ...
 *     return <Toast message={message} onDismiss={dismiss} />;
 *   }
 */

// ── Hook ────────────────────────────────────────────────
export function useToast(duration = 3500) {
  const [message, setMessage] = useState(null);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    setMessage(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const show = useCallback((type, text) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMessage({ type, text });
    timerRef.current = setTimeout(() => setMessage(null), duration);
  }, [duration]);

  const showSuccess = useCallback((text) => show('success', text), [show]);
  const showError   = useCallback((text) => show('error', text), [show]);

  return { message, showSuccess, showError, dismiss };
}

// ── Component ───────────────────────────────────────────
export function Toast({ message, onDismiss, className = '' }) {
  if (!message) return null;

  const isSuccess = message.type === 'success';

  return (
    <div
      role="alert"
      className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-md border transition-all duration-300 ${
        isSuccess
          ? 'bg-green-50 border-green-200 text-green-800'
          : 'bg-red-50 border-red-200 text-red-800'
      } ${className}`}
    >
      {isSuccess
        ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
        : <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
      <span className="text-sm font-medium flex-1">{message.text}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="p-1 rounded-lg hover:bg-black/5 transition-colors shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
